'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Bot,
  BrainCircuit,
  Check,
  ChevronDown,
  ClipboardCheck,
  CloudCog,
  Code2,
  Factory,
  Gauge,
  GraduationCap,
  Handshake,
  HeartPulse,
  Layers3,
  Lightbulb,
  LockKeyhole,
  Menu,
  Network,
  Route,
  ShieldCheck,
  Sparkles,
  Truck,
  UsersRound,
  Warehouse,
  Workflow,
  X,
  Zap,
} from 'lucide-react'
import {
  SiAnthropic,
  SiGooglegemini,
  SiN8N,
  SiOpenai,
  SiPerplexity,
} from 'react-icons/si'
import FloatingWhatsAppButton from '@/components/FloatingWhatsAppButton'
import styles from './landing.module.css'

const calendlyUrl = 'https://calendly.com/sispro/30min'

const services = [
  {
    number: '01',
    icon: Workflow,
    title: 'Transformación digital',
    promise: 'Procesos más simples. Equipos más productivos.',
    description:
      'Detectamos dónde se pierde tiempo y diseñamos una operación más ágil, conectada y medible.',
    outcomes: ['Digitalización de procesos', 'Automatización operativa', 'Menos tareas manuales'],
  },
  {
    number: '02',
    icon: BrainCircuit,
    title: 'Inteligencia artificial',
    promise: 'IA útil, aplicada a resultados concretos.',
    description:
      'Convertimos el conocimiento y los datos de su empresa en asistentes que ayudan a decidir y ejecutar mejor.',
    outcomes: ['Asistentes inteligentes', 'Búsqueda documental', 'Análisis de información'],
  },
  {
    number: '03',
    icon: Code2,
    title: 'Software a la medida',
    promise: 'Tecnología que se adapta a su negocio.',
    description:
      'Creamos y modernizamos soluciones cuando los sistemas actuales ya no acompañan el crecimiento.',
    outcomes: ['Aplicaciones web', 'Integraciones', 'Modernización de sistemas'],
  },
  {
    number: '04',
    icon: CloudCog,
    title: 'Servicios gestionados',
    promise: 'Continuidad, seguridad y tranquilidad.',
    description:
      'Cuidamos la operación tecnológica para que su equipo pueda concentrarse en el negocio.',
    outcomes: ['Infraestructura y cloud', 'Seguridad y monitoreo', 'Soporte y mantenimiento'],
  },
]

const useCases = [
  {
    icon: Warehouse,
    tag: 'Distribución',
    title: 'Del Excel disperso a una operación visible',
    description:
      'Unificamos pedidos, inventario y seguimiento comercial para reducir reprocesos y tomar decisiones con información confiable.',
    result: 'Menos errores · más control',
  },
  {
    icon: Factory,
    tag: 'Manufactura',
    title: 'Procesos de planta que avanzan sin fricción',
    description:
      'Digitalizamos registros y conectamos áreas para detectar cuellos de botella antes de que afecten la entrega.',
    result: 'Más productividad · menos espera',
  },
  {
    icon: HeartPulse,
    tag: 'Servicios y salud',
    title: 'Información disponible cuando importa',
    description:
      'Organizamos documentos y automatizamos consultas internas para liberar tiempo del equipo y mejorar la atención.',
    result: 'Respuestas ágiles · equipos enfocados',
  },
  {
    icon: Truck,
    tag: 'Logística',
    title: 'Sistemas que conversan entre sí',
    description:
      'Integramos plataformas y automatizamos el flujo de datos para reducir la digitación y mejorar la trazabilidad.',
    result: 'Más trazabilidad · menos reproceso',
  },
]

const aiEcosystem = [
  { name: 'ChatGPT', detail: 'Asistentes', icon: SiOpenai },
  { name: 'Claude', detail: 'Análisis', icon: SiAnthropic },
  { name: 'Gemini', detail: 'Workspace', icon: SiGooglegemini },
  { name: 'Perplexity', detail: 'Investigación', icon: SiPerplexity },
  { name: 'n8n', detail: 'Agentes y flujos', icon: SiN8N },
]

const faq = [
  {
    question: '¿Trabajan solo con empresas que ya tienen un área de tecnología?',
    answer:
      'No. Podemos convertirnos en su departamento de tecnología externo o complementar al equipo interno. El modelo se adapta a la madurez y al tamaño de la empresa.',
  },
  {
    question: '¿El diagnóstico inicial tiene costo?',
    answer:
      'La conversación de diagnóstico ejecutivo es gratuita. En 30 minutos revisamos prioridades, fricciones operativas y riesgos. Si existe una oportunidad clara, proponemos el siguiente paso y su alcance.',
  },
  {
    question: '¿Debemos cambiar todos nuestros sistemas?',
    answer:
      'Normalmente no. Primero aprovechamos mejor lo que ya existe, integramos donde sea posible y modernizamos solo aquello que limita el negocio. Cada inversión debe tener una razón empresarial.',
  },
  {
    question: '¿Implementan inteligencia artificial?',
    answer:
      'Sí, cuando resuelve un problema real. Empezamos por el proceso, la información y el resultado esperado; luego elegimos la tecnología adecuada, con controles de seguridad y adopción.',
  },
  {
    question: '¿Cómo funcionan los contratos recurrentes?',
    answer:
      'Definimos una hoja de ruta, prioridades, responsables y un ritmo de seguimiento. Así su empresa cuenta con criterio estratégico, capacidad de ejecución y cuidado continuo bajo un mismo acuerdo.',
  },
  {
    question: '¿Atienden empresas fuera de Bogotá?',
    answer:
      'Sí. Acompañamos empresas en Colombia con un modelo remoto y coordinamos atención presencial cuando el alcance lo requiere.',
  },
]

function trackLead(label: string) {
  if (typeof window !== 'undefined' && typeof window.fbq !== 'undefined') {
    window.fbq('track', 'Lead', { content_name: label })
  }
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false)

  const closeMenu = () => setMenuOpen(false)

  return (
    <div className={styles.siteShell}>
      <header className={styles.header}>
        <div className={styles.navWrap}>
          <Link href="#inicio" className={styles.logoLink} aria-label="Sispro Consultores, inicio">
            <Image
              src="/images/Logo_white.png"
              alt="Sispro Consultores"
              width={172}
              height={52}
              priority
              className={styles.logo}
            />
          </Link>

          <nav className={styles.desktopNav} aria-label="Navegación principal">
            <a href="#propuesta">Por qué Sispro</a>
            <a href="#soluciones">Soluciones</a>
            <a href="#metodo">Cómo trabajamos</a>
            <a href="#casos">Casos de uso</a>
          </nav>

          <div className={styles.navActions}>
            <Link href="/v1" className={styles.loginLink}>Clientes</Link>
            <a
              href={calendlyUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.navCta}
              onClick={() => trackLead('header_diagnostico')}
            >
              Hablemos <ArrowUpRight size={16} />
            </a>
          </div>

          <button
            type="button"
            className={styles.menuButton}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {menuOpen && (
          <nav className={styles.mobileNav} aria-label="Navegación móvil">
            <a href="#propuesta" onClick={closeMenu}>Por qué Sispro</a>
            <a href="#soluciones" onClick={closeMenu}>Soluciones</a>
            <a href="#metodo" onClick={closeMenu}>Cómo trabajamos</a>
            <a href="#casos" onClick={closeMenu}>Casos de uso</a>
            <Link href="/v1" onClick={closeMenu}>Acceso clientes</Link>
            <a
              href={calendlyUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.mobileCta}
              onClick={() => trackLead('mobile_diagnostico')}
            >
              Solicitar diagnóstico <ArrowRight size={18} />
            </a>
          </nav>
        )}
      </header>

      <main>
        <section id="inicio" className={styles.hero}>
          <div className={styles.heroGlow} aria-hidden="true" />
          <div className={styles.heroGrid} aria-hidden="true" />
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <p className={styles.eyebrow}>
                <span className={styles.eyebrowLine} />
                Su socio tecnológico en{' '}
                <span className={styles.colombiaMark}>
                  Colombia
                  <svg viewBox="0 0 84 24" aria-hidden="true">
                    <path d="M3 5.5C22 2.5 57 8 81 4" />
                    <path d="M4 12.5C26 9.5 59 15 80 11" />
                    <path d="M5 19C28 16 59 21 79 18" />
                  </svg>
                </span>
              </p>
              <h1>
                La tecnología debe ayudarle a <em>crecer.</em>
                <br />No convertirse en un problema.
              </h1>
              <p className={styles.heroLead}>
                Acompañamos a pequeñas y medianas empresas a reducir tareas manuales,
                tomar mejores decisiones y operar con tranquilidad. Estrategia y ejecución,
                con un equipo que piensa como parte del suyo.
              </p>
              <div className={styles.heroActions}>
                <a
                  href={calendlyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.primaryButton}
                  onClick={() => trackLead('hero_diagnostico')}
                >
                  Solicitar diagnóstico ejecutivo <ArrowRight size={19} />
                </a>
                <a href="#metodo" className={styles.textButton}>
                  Conocer nuestro enfoque <ArrowRight size={17} />
                </a>
              </div>
              <p className={styles.heroNote}>
                <Check size={16} /> Conversación de 30 minutos · Sin compromiso · En lenguaje de negocio
              </p>
            </div>

            <div className={styles.heroVisual} aria-label="De la fricción operativa a una operación que avanza">
              <div className={styles.visualTopline}>
                <span>ENFOQUE SISPRO</span>
                <span className={styles.visualStatus}><i /> Estrategia antes que herramientas</span>
              </div>
              <div className={styles.visualCanvas}>
                <div className={styles.visualStatement}>
                  <span className={styles.visualIndex}>01</span>
                  <p>Entender primero.<br /><em>Transformar lo que importa.</em></p>
                </div>
                <div className={styles.signalMap}>
                  <div className={styles.signalColumn}>
                    <span>Lo que hoy frena</span>
                    <ul>
                      <li>Procesos manuales</li>
                      <li>Información dispersa</li>
                      <li>Decisiones reactivas</li>
                    </ul>
                  </div>
                  <div className={styles.signalArrow}><ArrowRight size={20} /></div>
                  <div className={`${styles.signalColumn} ${styles.signalDestination}`}>
                    <span>Una operación que avanza</span>
                    <ul>
                      <li><Check size={14} /> Operación conectada</li>
                      <li><Check size={14} /> Prioridades claras</li>
                      <li><Check size={14} /> Crecimiento sostenible</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className={styles.visualMetric}>
                <div><span>01</span><p><strong>Estrategia</strong><small>Definir el rumbo</small></p></div>
                <div><span>02</span><p><strong>Ejecución</strong><small>Convertirlo en acción</small></p></div>
                <div><span>03</span><p><strong>Evolución</strong><small>Mejorar continuamente</small></p></div>
              </div>
            </div>
          </div>

          <div className={styles.sectorStrip}>
            <p>Experiencia para empresas que mueven a Colombia</p>
            <div className={styles.sectors}>
              <span><Factory size={17} /> Manufactura</span>
              <span><Warehouse size={17} /> Distribución</span>
              <span><Truck size={17} /> Logística</span>
              <span><HeartPulse size={17} /> Salud</span>
              <span><GraduationCap size={17} /> Educación</span>
              <span><UsersRound size={17} /> Servicios</span>
            </div>
          </div>
        </section>

        <section id="propuesta" className={styles.propositionSection}>
          <div className={styles.sectionInner}>
            <div className={styles.splitHeading}>
              <div>
                <p className={styles.kicker}>El reto no es comprar más tecnología</p>
                <h2>Es saber qué hacer, en qué orden y con quién.</h2>
              </div>
              <p>
                Cuando nadie conecta la estrategia del negocio con la tecnología, aparecen
                soluciones aisladas, costos ocultos y proyectos que no generan adopción.
                Sispro convierte esa complejidad en una hoja de ruta accionable.
              </p>
            </div>

            <div className={styles.changeGrid}>
              <article>
                <span className={styles.changeNumber}>01</span>
                <Gauge size={28} />
                <h3>Más eficiencia</h3>
                <p>Eliminamos tareas repetitivas y reducimos los tiempos que frenan la operación.</p>
              </article>
              <article>
                <span className={styles.changeNumber}>02</span>
                <BarChart3 size={28} />
                <h3>Mejores decisiones</h3>
                <p>Organizamos la información para que la gerencia vea lo importante y actúe a tiempo.</p>
              </article>
              <article>
                <span className={styles.changeNumber}>03</span>
                <ShieldCheck size={28} />
                <h3>Más tranquilidad</h3>
                <p>Anticipamos riesgos y cuidamos la continuidad para que la tecnología deje de ser una preocupación.</p>
              </article>
              <article>
                <span className={styles.changeNumber}>04</span>
                <Route size={28} />
                <h3>Crecimiento sostenible</h3>
                <p>Construimos capacidades que evolucionan con la empresa, no parches que se quedan cortos.</p>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.peopleSection} aria-labelledby="personas-title">
          <div className={styles.sectionInner}>
            <div className={styles.peopleIntro}>
              <div>
                <p className={styles.kicker}>La transformación se vive en el trabajo</p>
                <h2 id="personas-title">Tecnología pensada para la gente que mueve su empresa.</h2>
              </div>
              <p>
                Desde la planta hasta la gerencia, el cambio tiene sentido cuando las personas
                trabajan con más claridad, colaboran mejor y pueden concentrarse en lo importante.
              </p>
            </div>

            <div className={styles.peopleMosaic}>
              <figure className={`${styles.peoplePhoto} ${styles.peopleFeature}`}>
                <Image
                  src="/images/landing-people/equipo-colombiano.jpg"
                  alt="Equipo colombiano colaborando alrededor de una mesa de trabajo"
                  fill
                  sizes="(max-width: 900px) 100vw, 58vw"
                  className={styles.peopleImage}
                />
                <figcaption><span>Dirección y equipos</span><strong>Decidir juntos, avanzar con claridad.</strong></figcaption>
              </figure>

              <div className={styles.peopleSide}>
                <figure className={`${styles.peoplePhoto} ${styles.peopleWide}`}>
                  <Image
                    src="/images/landing-people/manufactura-colombiana.jpg"
                    alt="Profesionales colombianos coordinando un proceso de manufactura"
                    fill
                    sizes="(max-width: 900px) 100vw, 42vw"
                    className={styles.peopleImage}
                  />
                  <figcaption><span>Manufactura</span><strong>Procesos que fluyen.</strong></figcaption>
                </figure>

                <div className={styles.peoplePair}>
                  <figure className={styles.peoplePhoto}>
                    <Image
                      src="/images/landing-people/equipo-contable.jpg"
                      alt="Equipo contable colombiano revisando información financiera"
                      fill
                      sizes="(max-width: 680px) 100vw, 21vw"
                      className={styles.peopleImage}
                    />
                    <figcaption><span>Contabilidad</span><strong>Información que da confianza.</strong></figcaption>
                  </figure>
                  <figure className={styles.peoplePhoto}>
                    <Image
                      src="/images/landing-people/equipo-comercial-logistico.jpg"
                      alt="Equipo comercial y logístico colombiano coordinando una operación"
                      fill
                      sizes="(max-width: 680px) 100vw, 21vw"
                      className={styles.peopleImage}
                    />
                    <figcaption><span>Comercial y logística</span><strong>Equipos siempre conectados.</strong></figcaption>
                  </figure>
                </div>
              </div>
            </div>

            <p className={styles.peopleClosing}>
              <span>Hecho para empresas colombianas</span>
              La tecnología correcta se nota en el día a día de las personas.
            </p>
          </div>
        </section>

        <section id="soluciones" className={styles.servicesSection}>
          <div className={styles.sectionInner}>
            <div className={styles.servicesIntro}>
              <div>
                <p className={styles.kickerLight}>Una visión integral</p>
                <h2>Cuatro capacidades.<br />Un solo socio responsable.</h2>
              </div>
              <p>
                No tiene que coordinar múltiples proveedores ni traducir necesidades entre ellos.
                Articulamos estrategia, implementación y operación con un objetivo común: que su empresa avance.
              </p>
            </div>

            <div className={styles.aiEcosystem}>
              <div className={styles.aiEcosystemCopy}>
                <div className={styles.aiEcosystemLabel}>
                  <Sparkles size={16} /> IA que su equipo ya reconoce
                </div>
                <h3>Del chat a agentes que trabajan con sus procesos.</h3>
                <p>
                  Integramos las mejores herramientas con sus documentos, datos y flujos de trabajo,
                  con seguridad y un propósito empresarial claro.
                </p>
              </div>
              <div className={styles.aiToolArea}>
                <div className={styles.aiToolGrid} aria-label="Herramientas de inteligencia artificial que integramos">
                  {aiEcosystem.map((tool) => {
                    const Icon = tool.icon
                    return (
                      <div className={styles.aiTool} key={tool.name}>
                        <Icon size={22} aria-hidden="true" />
                        <span><strong>{tool.name}</strong><small>{tool.detail}</small></span>
                      </div>
                    )
                  })}
                  <div className={`${styles.aiTool} ${styles.aiToolCustom}`}>
                    <Bot size={22} aria-hidden="true" />
                    <span><strong>Agentes a medida</strong><small>Su operación</small></span>
                  </div>
                </div>
                <p className={styles.aiToolNote}>
                  Seleccionamos la tecnología según el reto. Sin dependencia de una sola marca.
                </p>
              </div>
            </div>

            <div className={styles.serviceList}>
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <article key={service.number} className={styles.serviceCard}>
                    <div className={styles.serviceMeta}>
                      <span>{service.number}</span>
                      <Icon size={26} />
                    </div>
                    <h3>{service.title}</h3>
                    <h4>{service.promise}</h4>
                    <p>{service.description}</p>
                    <ul>
                      {service.outcomes.map((outcome) => (
                        <li key={outcome}><Check size={15} /> {outcome}</li>
                      ))}
                    </ul>
                  </article>
                )
              })}
            </div>

            <div className={styles.servicesClosing}>
              <Sparkles size={22} />
              <p><strong>La solución no empieza por una herramienta.</strong> Empieza por entender qué resultado necesita el negocio.</p>
            </div>
          </div>
        </section>

        <section id="casos" className={styles.casesSection}>
          <div className={styles.sectionInner}>
            <div className={styles.centerHeading}>
              <p className={styles.kicker}>De problemas cotidianos a ventajas operativas</p>
              <h2>Así se ve la transformación en la práctica.</h2>
              <p>Escenarios representativos de cómo abordamos retos frecuentes en pequeñas y medianas empresas.</p>
            </div>

            <div className={styles.caseGrid}>
              {useCases.map((item) => {
                const Icon = item.icon
                return (
                  <article key={item.title} className={styles.caseCard}>
                    <div className={styles.caseHeader}>
                      <Icon size={24} />
                      <span>{item.tag}</span>
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <strong>{item.result}</strong>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        <section id="metodo" className={styles.methodSection}>
          <div className={styles.sectionInner}>
            <div className={styles.methodLayout}>
              <div className={styles.methodIntro}>
                <p className={styles.kicker}>Nuestro método</p>
                <h2>Claridad antes que complejidad.</h2>
                <p>
                  Un acompañamiento cercano, con prioridades visibles y resultados que la gerencia puede entender.
                </p>
                <a
                  href={calendlyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.darkButton}
                  onClick={() => trackLead('metodo_diagnostico')}
                >
                  Conversemos sobre su empresa <ArrowRight size={18} />
                </a>
              </div>

              <ol className={styles.steps}>
                <li>
                  <span>01</span>
                  <div><ClipboardCheck size={24} /><h3>Entendemos</h3><p>Escuchamos a la gerencia, observamos la operación e identificamos dónde están las fricciones y oportunidades.</p></div>
                </li>
                <li>
                  <span>02</span>
                  <div><Lightbulb size={24} /><h3>Priorizamos</h3><p>Construimos una hoja de ruta por impacto, urgencia y viabilidad. Sin proyectos innecesarios.</p></div>
                </li>
                <li>
                  <span>03</span>
                  <div><Zap size={24} /><h3>Implementamos</h3><p>Ejecutamos por etapas, involucramos al equipo y medimos el cambio desde el negocio.</p></div>
                </li>
                <li>
                  <span>04</span>
                  <div><Handshake size={24} /><h3>Acompañamos</h3><p>Operamos, mejoramos y ajustamos la estrategia a medida que la empresa evoluciona.</p></div>
                </li>
              </ol>
            </div>
          </div>
        </section>

        <section className={styles.partnershipSection}>
          <div className={styles.sectionInner}>
            <div className={styles.partnershipGrid}>
              <div className={styles.partnershipCopy}>
                <p className={styles.kickerLight}>Más que un proveedor</p>
                <h2>Su departamento de tecnología externo.</h2>
                <p>
                  La confianza se construye con criterio, continuidad y responsabilidad.
                  Por eso trabajamos para conocer su empresa durante años, no para vender un proyecto aislado.
                </p>
              </div>
              <div className={styles.differenceList}>
                <div><span>01</span><p><strong>Hablamos de negocio.</strong> Traducimos la tecnología en impacto, riesgos y decisiones.</p></div>
                <div><span>02</span><p><strong>Somos independientes.</strong> Recomendamos lo que la empresa necesita, no lo que un fabricante quiere vender.</p></div>
                <div><span>03</span><p><strong>Nos hacemos responsables.</strong> Unimos consultoría y ejecución para que las ideas sí lleguen a la operación.</p></div>
                <div><span>04</span><p><strong>Pensamos a largo plazo.</strong> Cada avance prepara a la empresa para el siguiente nivel.</p></div>
              </div>
            </div>
          </div>
        </section>

        <section id="recursos" className={styles.resourceSection}>
          <div className={styles.resourceInner}>
            <div className={styles.resourceIcon}><Layers3 size={34} /></div>
            <div className={styles.resourceCopy}>
              <p className={styles.kicker}>Recurso para gerentes</p>
              <h2>Guía: 12 señales de que su empresa necesita una hoja de ruta tecnológica</h2>
              <p>
                Una evaluación práctica para detectar procesos manuales, riesgos operativos y oportunidades de automatización antes de invertir.
              </p>
            </div>
            <a
              href="https://wa.me/573057216462?text=Hola%2C%20quiero%20recibir%20la%20gu%C3%ADa%20de%20madurez%20tecnol%C3%B3gica%20para%20pymes."
              target="_blank"
              rel="noreferrer"
              className={styles.resourceButton}
              onClick={() => trackLead('lead_magnet_guia')}
            >
              Solicitar la guía <ArrowUpRight size={18} />
            </a>
          </div>
        </section>

        <section className={styles.trustSection}>
          <div className={styles.sectionInner}>
            <div className={styles.trustGrid}>
              <div>
                <p className={styles.kicker}>La experiencia que buscamos construir</p>
                <h2>Un equipo que entiende, responde y se queda.</h2>
              </div>
              <div className={styles.trustQuote}>
                <Bot size={30} />
                <blockquote>
                  “No queremos que nos llamen solo cuando algo falla. Queremos estar en la conversación cuando la empresa decide cómo crecer.”
                </blockquote>
                <p>El compromiso de Sispro Consultores</p>
              </div>
            </div>
            <div className={styles.trustSignals}>
              <span><Network size={20} /> Visión integral</span>
              <span><LockKeyhole size={20} /> Confidencialidad</span>
              <span><Handshake size={20} /> Relación de largo plazo</span>
              <span><Check size={20} /> Responsabilidad de punta a punta</span>
            </div>
          </div>
        </section>

        <section id="preguntas" className={styles.faqSection}>
          <div className={styles.sectionInner}>
            <div className={styles.faqLayout}>
              <div className={styles.faqIntro}>
                <p className={styles.kicker}>Preguntas frecuentes</p>
                <h2>Antes de empezar.</h2>
                <p>Respuestas claras para tomar una buena decisión.</p>
              </div>
              <div className={styles.faqList}>
                {faq.map((item) => (
                  <details key={item.question}>
                    <summary>{item.question}<ChevronDown size={20} /></summary>
                    <p>{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="diagnostico" className={styles.finalCtaSection}>
          <div className={styles.finalCtaGlow} aria-hidden="true" />
          <div className={styles.finalCtaInner}>
            <p className={styles.kickerLight}>El siguiente paso es una conversación</p>
            <h2>Convirtamos sus retos tecnológicos en una hoja de ruta para crecer.</h2>
            <p>
              En 30 minutos identificamos prioridades, oportunidades y el mejor punto de partida para su empresa.
            </p>
            <div className={styles.finalActions}>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.primaryButton}
                onClick={() => trackLead('footer_diagnostico')}
              >
                Agendar diagnóstico ejecutivo <ArrowRight size={19} />
              </a>
              <a href="tel:+573057216462" className={styles.finalPhone}>+57 305 721 6462</a>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerTop}>
          <div>
            <Image src="/images/Logo_white.png" alt="Sispro Consultores" width={170} height={52} className={styles.footerLogo} />
            <p>El socio tecnológico de las pequeñas y medianas empresas.</p>
          </div>
          <div>
            <h3>Explorar</h3>
            <a href="#propuesta">Por qué Sispro</a>
            <a href="#soluciones">Soluciones</a>
            <a href="#metodo">Cómo trabajamos</a>
            <a href="#casos">Casos de uso</a>
          </div>
          <div>
            <h3>Contacto</h3>
            <a href={calendlyUrl} target="_blank" rel="noreferrer">Agendar una conversación</a>
            <a href="https://wa.me/573057216462" target="_blank" rel="noreferrer">WhatsApp</a>
            <a href="tel:+573057216462">+57 305 721 6462</a>
            <span className={styles.footerCountry}>
              Colombia
              <i aria-hidden="true"><b /><b /><b /></i>
            </span>
          </div>
          <div>
            <h3>Clientes</h3>
            <Link href="/v1">Acceso a la plataforma</Link>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span>© {new Date().getFullYear()} Sispro Consultores. Todos los derechos reservados.</span>
          <span>Estrategia · Tecnología · Crecimiento</span>
        </div>
      </footer>

      <FloatingWhatsAppButton
        phoneNumber="573057216462"
        message="Hola, quiero conversar sobre los retos tecnológicos y de crecimiento de mi empresa."
        ariaLabel="Hablar con Sispro por WhatsApp"
      />
    </div>
  )
}
